import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import {
  FDECustomerUnit,
  GeckoAssetHealthAction,
  GeckoAssetHealthPlan,
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoEntry,
  GeckoEntryBase,
  GeckoInspection,
  GeckoKestrelModel,
  GeckoMarkupLayer,
  GeckoRiskAssessmentConfig,
  GeckoSiteConfig,
  GeckoUnit,
  RolodexEntryType,
} from 'types'

export type NeededDataFetch =
  | {
      isLoading: true
      forceRefresh: () => Promise<void>
    }
  | {
      isLoading: false
      forceRefresh: () => Promise<void>
      // Core Types
      allUnitsAtSite: ReadonlyArray<GeckoUnit>
      allComponentsAtSite: ReadonlyArray<GeckoComponent>
      allInspectionsAtSite: ReadonlyArray<GeckoInspection>
      allAssetModelsAtSite: ReadonlyArray<GeckoAssetModel>
      allComponentDataFramesAtSite: ReadonlyArray<GeckoComponentDataFrame>
      allComponentDatasetsAtSite: ReadonlyArray<GeckoComponentDataset>
      allComponentDefaultsAtSite: ReadonlyArray<GeckoComponentDefaults>
      allMarkupLayersAtSite: ReadonlyArray<GeckoMarkupLayer>
      allKestrelModelsAtSite: ReadonlyArray<GeckoKestrelModel>
      // FDE Types
      siteConfig: GeckoSiteConfig | undefined
      riskAssessmentConfig: GeckoRiskAssessmentConfig | undefined
      allFDECustomerUnitsAtSite: ReadonlyArray<GeckoEntryBase<FDECustomerUnit>>
      allComponentHealthPlansAtSite: ReadonlyArray<GeckoAssetHealthPlan>
      allComponentHealthPlanActionsAtSite: ReadonlyArray<GeckoAssetHealthAction>
      allComponentHealthPlanActionSpecsAtSite: ReadonlyArray<GeckoEntry>
    }

/** Fetches all the data needed for the SiteView page from the Rolodex */
export function useFetchNeededSiteDataFromRolodex(
  siteID: string
): NeededDataFetch {
  const {
    isLoading,
    siteConfigs: [siteConfig],
    riskAssessmentConfigs: [riskAssessmentConfig],
    units: allUnitsAtSite,
    components: allComponentsAtSite,
    inspections: allInspectionsAtSite,
    assetModels: allAssetModelsAtSite,
    componentDataFrames: allComponentDataFramesAtSite,
    componentDatasets: allComponentDatasetsAtSite,
    componentDefaults: allComponentDefaultsAtSite,
    markupLayers: allMarkupLayersAtSite,
    kestrelModels: allKestrelModelsAtSite,
    entries: allEntriesAtSite,
    forceRefresh,
  } = useRolodexEntries(
    {
      type: [
        // Core Types
        RolodexEntryType.UNIT,
        RolodexEntryType.COMPONENT,
        RolodexEntryType.INSPECTION,
        RolodexEntryType.ASSET_MODEL,
        RolodexEntryType.COMPONENT_DATA_FRAME,
        RolodexEntryType.COMPONENT_DATASET,
        RolodexEntryType.COMPONENT_DEFAULTS,
        RolodexEntryType.MARKUP_LAYER,
        RolodexEntryType.KESTREL_MODEL,
        // FDE Types
        RolodexEntryType.FDE_UNIT,
        RolodexEntryType.COMPONENT_HEALTH_PLAN,
        RolodexEntryType.COMPONENT_HEALTH_PLAN_ACTION,
        RolodexEntryType.REPAIR_PLAN,
        RolodexEntryType.DERATING_SCENARIO,
        RolodexEntryType.PIPE_WRAP,
        RolodexEntryType.REPLACEMENT_PLAN,
        RolodexEntryType.SITE_CONFIG,
        RolodexEntryType.RISK_ASSESSMENT_CONFIG,
      ],
      tags: { [RolodexEntryType.SITE]: siteID },
      limit: 100000000,
    },
    {
      swrOptions: {
        // Make this useSWRImmutable
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        // revalidate data if page is navigated away from then back to (e.g. if user goes to edit things in AHP)
        revalidateOnMount: true,
      },
    }
  )

  return useMemo((): NeededDataFetch => {
    if (isLoading) {
      return {
        isLoading,
        forceRefresh,
      }
    }

    return {
      isLoading,
      forceRefresh,
      // Core Types
      allUnitsAtSite,
      allComponentsAtSite,
      allInspectionsAtSite,
      allAssetModelsAtSite,
      allComponentDatasetsAtSite,
      allComponentDataFramesAtSite,
      allComponentDefaultsAtSite,
      allMarkupLayersAtSite,
      allKestrelModelsAtSite,
      // FDE Types
      siteConfig,
      riskAssessmentConfig,
      allComponentHealthPlansAtSite: allEntriesAtSite.filter(
        (curEntry): curEntry is GeckoAssetHealthPlan => {
          return curEntry.type === RolodexEntryType.COMPONENT_HEALTH_PLAN
        }
      ),
      allComponentHealthPlanActionsAtSite: allEntriesAtSite.filter(
        (curEntry): curEntry is GeckoAssetHealthAction => {
          return curEntry.type === RolodexEntryType.COMPONENT_HEALTH_PLAN_ACTION
        }
      ),
      allFDECustomerUnitsAtSite: allEntriesAtSite.filter(
        (curEntry): curEntry is GeckoEntryBase<FDECustomerUnit> => {
          return curEntry.type === RolodexEntryType.FDE_UNIT
        }
      ),
      allComponentHealthPlanActionSpecsAtSite: allEntriesAtSite.filter(
        (curEntry) => {
          const ahpActionSpecTypes: string[] = [
            RolodexEntryType.REPAIR_PLAN,
            RolodexEntryType.DERATING_SCENARIO,
            RolodexEntryType.PIPE_WRAP,
            RolodexEntryType.REPLACEMENT_PLAN,
          ]
          return ahpActionSpecTypes.includes(curEntry.type)
        }
      ),
    }
  }, [
    isLoading,
    forceRefresh,
    allUnitsAtSite,
    allComponentsAtSite,
    allInspectionsAtSite,
    allAssetModelsAtSite,
    allComponentDatasetsAtSite,
    allComponentDataFramesAtSite,
    allComponentDefaultsAtSite,
    allMarkupLayersAtSite,
    allKestrelModelsAtSite,
    siteConfig,
    riskAssessmentConfig,
    allEntriesAtSite,
  ])
}
